// --- UPDATE (FlowSense AI Explainer + real-data pass)
//   • Crash fixes: numbers sent as strings ("12.50") broke `.toFixed` and took the whole widget
//     down; numeric item ids broke search; sorting by Item ID compared text by subtraction (NaN).
//   • A zero total value showed "NaN%"; missing summary values show "—".
//   • Category cards are readable in dark mode; CSV export of every class added.
//   • "Explain this ABC split" FlowSense AI explainer: how the real split compares with the
//     80 / 15 / 5 rule, the single most valuable item, A-items with no stock on hand.
// --- END UPDATE
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box, Heading, Spinner, Alert, AlertIcon, SimpleGrid, Stat, StatLabel,
  StatNumber, StatHelpText, useColorModeValue, Text, VStack, HStack,
  Icon, IconButton, Tooltip, Badge, Progress, Button, Flex,
  Table, Thead, Tbody, Tr, Th, Td, TableContainer, Tabs, TabList,
  Tab, TabPanels, TabPanel, Input, InputGroup, InputLeftElement,
  Select, Divider, useToast, Menu, MenuButton, MenuList, MenuItem,
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalFooter,
  ModalCloseButton
} from '@chakra-ui/react';
import ReactApexChart from 'react-apexcharts';
import { 
  FaChartPie, FaDownload, FaFilter, FaSearch, FaBox,
  FaDollarSign, FaWarehouse, FaExclamationTriangle 
} from 'react-icons/fa';
import { RepeatIcon, InfoIcon, DownloadIcon } from '@chakra-ui/icons';
import analyticsService from '../../services/analyticsService';
import Card from '../card/Cards';
import FlowSenseAIExplainer from '../ai/FlowSenseAIExplainer'; // --- UPDATE

// --- UPDATE: numbers may arrive as strings; unknown stays "—"
const n = (v) => (v === null || v === undefined || v === '' || !Number.isFinite(Number(v)) ? null : Number(v));
const money2 = (v) => (n(v) === null ? '—' : `$${n(v).toFixed(2)}`);
const pctOf = (part, whole) => (n(whole) && n(part) !== null ? (n(part) / n(whole)) * 100 : null);
const pctText = (p, d = 1) => (p === null ? '—' : `${p.toFixed(d)}%`);
const csvCell = (v) => { const s = v === null || v === undefined ? '' : String(v); return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s; };
// --- END UPDATE

// Item Details Modal
const ItemDetailsModal = ({ isOpen, onClose, item, category }) => {
  const getCategoryColor = (cat) => {
    switch (cat) {
      case 'A': return 'blue';
      case 'B': return 'green';
      case 'C': return 'orange';
      default: return 'gray';
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          <HStack>
            <Icon as={FaBox} />
            <Text>Item Details</Text>
            <Badge colorScheme={getCategoryColor(category)}>{category}-Class</Badge>
          </HStack>
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          {item ? (
            <VStack align="stretch" spacing={3}>
              <Box>
                <Text fontSize="sm" color="gray.500">Item ID</Text>
                <Text fontWeight="bold">{item.item_id}</Text>
              </Box>
              <Box>
                <Text fontSize="sm" color="gray.500">Description</Text>
                <Text fontWeight="bold">{item.description || 'N/A'}</Text>
              </Box>
              <Divider />
              <SimpleGrid columns={2} spacing={3}>
                <Box>
                  <Text fontSize="sm" color="gray.500">Unit Cost</Text>
                  <Text fontWeight="bold">{money2(item.unit_cost)}</Text>
                </Box>
                <Box>
                  <Text fontSize="sm" color="gray.500">Quantity</Text>
                  <Text fontWeight="bold">{n(item.quantity) ?? '—'}</Text>
                </Box>
                <Box>
                  <Text fontSize="sm" color="gray.500">Total Value</Text>
                  <Text fontWeight="bold" color="blue.500">
                    {money2(item.total_value)}
                  </Text>
                </Box>
                <Box>
                  <Text fontSize="sm" color="gray.500">% of Total</Text>
                  <Text fontWeight="bold">{n(item.percentage) === null ? '—' : `${n(item.percentage).toFixed(2)}%`}</Text>
                </Box>
              </SimpleGrid>
            </VStack>
          ) : (
            <Text>No item data available</Text>
          )}
        </ModalBody>
        <ModalFooter>
          <Button onClick={onClose}>Close</Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

// Item Table Component
const ItemTable = ({ items, category, onItemClick }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [sortField, setSortField] = useState('total_value');
  const [sortOrder, setSortOrder] = useState('desc');

  const hoverBg = useColorModeValue('gray.50', 'gray.700');

  const getCategoryColor = (cat) => {
    switch (cat) {
      case 'A': return 'blue';
      case 'B': return 'green';
      case 'C': return 'orange';
      default: return 'gray';
    }
  };

  const filteredItems = useMemo(() => {
    // --- UPDATE: string-safe search, text sort for ids, numeric sort for numbers
    const q = searchTerm.toLowerCase();
    let filtered = (items || []).filter(item =>
      String(item.item_id ?? '').toLowerCase().includes(q) ||
      String(item.description ?? '').toLowerCase().includes(q)
    );

    filtered.sort((a, b) => {
      if (sortField === 'item_id') {
        const r = String(a.item_id ?? '').localeCompare(String(b.item_id ?? ''), undefined, { numeric: true });
        return sortOrder === 'asc' ? r : -r;
      }
      const aVal = n(a[sortField]) ?? 0;
      const bVal = n(b[sortField]) ?? 0;
      return sortOrder === 'asc' ? aVal - bVal : bVal - aVal;
    });
    // --- END UPDATE

    return filtered;
  }, [items, searchTerm, sortField, sortOrder]);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortOrder('desc');
    }
  };

  return (
    <VStack align="stretch" spacing={3}>
      <HStack spacing={3}>
        <InputGroup size="sm" maxW="300px">
          <InputLeftElement pointerEvents="none">
            <FaSearch color="gray" />
          </InputLeftElement>
          <Input
            placeholder="Search items..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </InputGroup>
        
        <Badge colorScheme={getCategoryColor(category)} fontSize="sm">
          {filteredItems.length} items
        </Badge>
      </HStack>

      <TableContainer>
        <Table size="sm" variant="simple">
          <Thead>
            <Tr>
              <Th cursor="pointer" onClick={() => handleSort('item_id')}>
                Item ID {sortField === 'item_id' && (sortOrder === 'asc' ? '↑' : '↓')}
              </Th>
              <Th>Description</Th>
              <Th isNumeric cursor="pointer" onClick={() => handleSort('unit_cost')}>
                Unit Cost {sortField === 'unit_cost' && (sortOrder === 'asc' ? '↑' : '↓')}
              </Th>
              <Th isNumeric cursor="pointer" onClick={() => handleSort('quantity')}>
                Qty {sortField === 'quantity' && (sortOrder === 'asc' ? '↑' : '↓')}
              </Th>
              <Th isNumeric cursor="pointer" onClick={() => handleSort('total_value')}>
                Total Value {sortField === 'total_value' && (sortOrder === 'asc' ? '↑' : '↓')}
              </Th>
              <Th isNumeric>% of Total</Th>
            </Tr>
          </Thead>
          <Tbody>
            {filteredItems.map((item, idx) => (
              <Tr
                key={item.item_id ?? idx}
                _hover={{ bg: hoverBg, cursor: 'pointer' }}
                onClick={() => onItemClick(item)}
              >
                <Td fontWeight="medium">{item.item_id}</Td>
                <Td isTruncated maxW="200px">{item.description || 'N/A'}</Td>
                <Td isNumeric>{money2(item.unit_cost)}</Td>
                <Td isNumeric>{n(item.quantity) ?? '—'}</Td>
                <Td isNumeric fontWeight="bold" color="blue.500">
                  {money2(item.total_value)}
                </Td>
                <Td isNumeric>{n(item.percentage) === null ? '—' : `${n(item.percentage).toFixed(2)}%`}</Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
      </TableContainer>

      {filteredItems.length === 0 && (
        <Alert status="info" borderRadius="md">
          <AlertIcon />
          No items found matching your search criteria.
        </Alert>
      )}
    </VStack>
  );
};

const ABCAnalysisWidget = () => {
  const [analysis, setAnalysis] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedItem, setSelectedItem] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [lastRefresh, setLastRefresh] = useState(null);

  const toast = useToast();
  const chartTextColor = useColorModeValue('gray.800', 'white');
  const cardBg = useColorModeValue('white', 'gray.800');
  const statBg = useColorModeValue('gray.50', 'gray.700');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  // --- UPDATE: category card backgrounds that work in dark mode
  const catBg = { A: useColorModeValue('blue.50', 'whiteAlpha.100'), B: useColorModeValue('teal.50', 'whiteAlpha.100'), C: useColorModeValue('orange.50', 'whiteAlpha.100') };
  const catText = useColorModeValue('gray.600', 'gray.300');
  // --- END UPDATE

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      
      console.log('[ABC-ANALYSIS] Fetching data...');
      const response = await analyticsService.getABCAnalysis();
      
      console.log('[ABC-ANALYSIS] Response:', response);
      
      if (response?.success && response.data) { // --- UPDATE: success without data is "no data"
        setAnalysis(response.data);
        setLastRefresh(new Date());
      } else {
        throw new Error(response?.message || 'Failed to fetch analysis.');
      }
    } catch (err) {
      console.error('[ABC-ANALYSIS] Error:', err);
      setError(err.message);
      toast({
        title: 'Failed to Load Analysis',
        description: err.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleItemClick = (item, category) => {
    setSelectedItem(item);
    setSelectedCategory(category);
    setIsModalOpen(true);
  };

  // --- UPDATE: CSV of every class
  const handleExportCsv = useCallback(() => {
    if (!analysis) return;
    const rows = [['Class', 'Item ID', 'Description', 'Unit Cost', 'Quantity', 'Total Value', '% of Total']];
    [['A', analysis.itemsA], ['B', analysis.itemsB], ['C', analysis.itemsC]].forEach(([c, list]) => (list || []).forEach(i => rows.push([c, i.item_id, i.description, n(i.unit_cost), n(i.quantity), n(i.total_value), n(i.percentage)])));
    const blob = new Blob([rows.map(r => r.map(csvCell).join(',')).join('\n')], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = `abc-analysis-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a); a.click(); document.body.removeChild(a); URL.revokeObjectURL(url);
  }, [analysis]);

  // --- UPDATE: FlowSense rule findings (computed before the early returns — hooks rule)
  const abcExplainer = useMemo(() => {
    const findings = [];
    const s = analysis?.summary;
    if (!s) return { findings, facts: {} };
    const totalCount = (n(s.countA) || 0) + (n(s.countB) || 0) + (n(s.countC) || 0);
    const valA = pctOf(s.valueA, s.totalValue); const cntA = pctOf(s.countA, totalCount);
    const valC = pctOf(s.valueC, s.totalValue); const cntC = pctOf(s.countC, totalCount);
    if (valA !== null && cntA !== null) findings.push({ severity: 'info', text: `A-class: ${n(s.countA)} items (${cntA.toFixed(0)}% of items) hold ${valA.toFixed(0)}% of inventory value — the classic split is about 20% of items for 80% of value.` });
    if (valC !== null && cntC !== null && cntC >= 40) findings.push({ severity: 'info', text: `C-class: ${cntC.toFixed(0)}% of items carry only ${valC.toFixed(1)}% of value — candidates for simpler reorder rules.` });
    const all = [...(analysis.itemsA || []), ...(analysis.itemsB || []), ...(analysis.itemsC || [])];
    const top = [...all].sort((a, b) => (n(b.total_value) || 0) - (n(a.total_value) || 0))[0];
    if (top && n(top.total_value) !== null) findings.push({ severity: 'info', text: `Most valuable line: ${top.description || top.item_id} — ${money2(top.total_value)} (${n(top.percentage) === null ? '—' : `${n(top.percentage).toFixed(1)}%`} of total).` });
    const emptyA = (analysis.itemsA || []).filter(i => n(i.quantity) === 0);
    if (emptyA.length) findings.push({ severity: 'high', text: `${emptyA.length} A-class item${emptyA.length > 1 ? 's have' : ' has'} zero quantity on hand: ${emptyA.slice(0, 3).map(i => i.description || i.item_id).join(', ')}${emptyA.length > 3 ? '…' : ''}.` });
    if (!n(s.totalValue)) findings.push({ severity: 'medium', text: 'Total inventory value is zero or missing — check unit costs in the inventory register.' });
    return {
      findings,
      facts: {
        summary: s, item_count: totalCount, a_value_share_pct: valA, a_count_share_pct: cntA,
        top_items: [...all].sort((a, b) => (n(b.total_value) || 0) - (n(a.total_value) || 0)).slice(0, 10).map(i => ({ id: i.item_id, description: i.description, value: n(i.total_value), qty: n(i.quantity) })),
        rule: 'A ≈ top 20% of items / 80% of value, B ≈ next 30% / 15%, C ≈ bottom 50% / 5%',
      },
    };
  }, [analysis]);
  // --- END UPDATE

  const handleExport = useCallback(() => {
    if (!analysis) return;

    const exportData = {
      timestamp: new Date().toISOString(),
      summary: analysis.summary,
      items: {
        A: analysis.itemsA,
        B: analysis.itemsB,
        C: analysis.itemsC,
      },
    };

    const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `abc-analysis-${new Date().toISOString()}.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    toast({
      title: 'Export Complete',
      description: 'ABC analysis data exported successfully.',
      status: 'success',
      duration: 3000,
    });
  }, [analysis, toast]);

  if (loading && !analysis) {
    return (
      <Card p={5}>
        <Flex justify="center" align="center" direction="column" py={8}>
          <Spinner size="xl" color="blue.500" thickness="4px" />
          <Text mt={4} color="gray.500">Loading ABC analysis...</Text>
        </Flex>
      </Card>
    );
  }

  if (error && !analysis) {
    return (
      <Card p={5}>
        <Alert status="error" borderRadius="md">
          <AlertIcon />
          <Box>
            <Text fontWeight="bold">Analysis Error</Text>
            <Text fontSize="sm">{error}</Text>
          </Box>
        </Alert>
      </Card>
    );
  }

  if (!analysis) {
    return (
      <Card p={5}>
        <Alert status="info" borderRadius="md">
          <AlertIcon />
          No data available for ABC analysis. Please check your inventory data.
        </Alert>
      </Card>
    );
  }

  // --- UPDATE: numeric summary with safe defaults
  const summary = {
    ...analysis.summary,
    countA: n(analysis.summary?.countA) || 0, countB: n(analysis.summary?.countB) || 0, countC: n(analysis.summary?.countC) || 0,
    valueA: n(analysis.summary?.valueA) || 0, valueB: n(analysis.summary?.valueB) || 0, valueC: n(analysis.summary?.valueC) || 0,
    totalValue: n(analysis.summary?.totalValue) || 0,
  };
  const totalItems = summary.countA + summary.countB + summary.countC;
  const share = (v) => pctOf(v, summary.totalValue);
  // --- END UPDATE

  // Chart options
  const donutChartOptions = {
    chart: {
      type: 'donut',
      foreColor: chartTextColor,
      toolbar: { show: false },
      animations: { enabled: true },
    },
    labels: ['A Items (High Value)', 'B Items (Medium Value)', 'C Items (Low Value)'],
    colors: ['#3182CE', '#38B2AC', '#DD6B20'],
    legend: {
      position: 'bottom',
      fontSize: '14px',
    },
    dataLabels: {
      enabled: true,
      formatter: (val) => `${val.toFixed(1)}%`,
    },
    plotOptions: {
      pie: {
        donut: {
          size: '65%',
          labels: {
            show: true,
            name: { show: true, fontSize: '14px' },
            value: {
              show: true,
              fontSize: '24px',
              fontWeight: 'bold',
              formatter: (val) => `$${parseFloat(val).toLocaleString()}`,
            },
            total: {
              show: true,
              label: 'Total Value',
              fontSize: '16px',
              color: chartTextColor,
              formatter: () => `$${summary.totalValue.toLocaleString()}`,
            },
          },
        },
      },
    },
    tooltip: {
      theme: 'dark',
      y: {
        formatter: (val) => `$${val.toLocaleString()}`,
      },
    },
  };

  const donutChartSeries = [summary.valueA, summary.valueB, summary.valueC];

  // Bar chart for item counts
  const barChartOptions = {
    chart: {
      type: 'bar',
      foreColor: chartTextColor,
      toolbar: { show: false },
    },
    plotOptions: {
      bar: {
        horizontal: false,
        columnWidth: '55%',
        borderRadius: 4,
        distributed: true,
      },
    },
    dataLabels: { enabled: true },
    xaxis: {
      categories: ['A Items', 'B Items', 'C Items'],
    },
    yaxis: {
      title: { text: 'Number of Items' },
    },
    colors: ['#3182CE', '#38B2AC', '#DD6B20'],
    legend: { show: false },
    tooltip: {
      theme: 'dark',
      y: {
        formatter: (val) => `${val} items`,
      },
    },
  };

  const barChartSeries = [
    { name: 'Items', data: [summary.countA, summary.countB, summary.countC] },
  ];

  return (
    <Card p={5}>
      {/* Header */}
      <Flex justify="space-between" align="center" mb={6} wrap="wrap" gap={3}>
        <HStack spacing={3}>
          <Icon as={FaChartPie} boxSize={6} color="blue.500" />
          <Heading size="lg">Inventory ABC Analysis</Heading>
          {lastRefresh && (
            <Tooltip label={`Last updated: ${lastRefresh.toLocaleString()}`} hasArrow>
              <Badge colorScheme="green" cursor="help">
                {Math.floor((new Date() - lastRefresh) / 60000)}m ago
              </Badge>
            </Tooltip>
          )}
        </HStack>

        <HStack spacing={2}>
          <Tooltip label="Refresh Analysis" hasArrow>
            <IconButton
              icon={<RepeatIcon />}
              size="sm"
              variant="ghost"
              onClick={fetchData}
              isLoading={loading}
              aria-label="Refresh"
            />
          </Tooltip>
          {/* --- UPDATE */}
          <Tooltip label="Export CSV (all classes)" hasArrow>
            <IconButton icon={<FaDownload />} size="sm" variant="ghost" onClick={handleExportCsv} aria-label="Export CSV" />
          </Tooltip>
          <Tooltip label="Export JSON" hasArrow>
            <IconButton
              icon={<DownloadIcon />}
              size="sm"
              variant="ghost"
              onClick={handleExport}
              aria-label="Export"
            />
          </Tooltip>
          <Menu>
            <Tooltip label="More Options" hasArrow>
              <MenuButton
                as={IconButton}
                icon={<InfoIcon />}
                size="sm"
                variant="ghost"
                aria-label="Info"
              />
            </Tooltip>
            <MenuList fontSize="sm">
              <MenuItem>
                <VStack align="start" spacing={1}>
                  <Text fontWeight="bold">ABC Classification</Text>
                  <Text fontSize="xs" color="gray.500">
                    A: Top 20% items = 80% value<br />
                    B: Next 30% items = 15% value<br />
                    C: Bottom 50% items = 5% value
                  </Text>
                </VStack>
              </MenuItem>
            </MenuList>
          </Menu>
        </HStack>
      </Flex>

      {/* Summary Statistics */}
      <SimpleGrid columns={{ base: 2, md: 4 }} spacing={4} mb={6}>
        <Stat bg={statBg} p={4} borderRadius="md" borderWidth="1px" borderColor={borderColor}>
          <StatLabel fontSize="xs">Total Items</StatLabel>
          <StatNumber fontSize="2xl">
            {totalItems}
          </StatNumber>
          <StatHelpText fontSize="xs">In inventory</StatHelpText>
        </Stat>

        <Stat bg={statBg} p={4} borderRadius="md" borderWidth="1px" borderColor={borderColor}>
          <StatLabel fontSize="xs">Total Value</StatLabel>
          <StatNumber fontSize="2xl" color="blue.500">
            ${summary.totalValue.toLocaleString()}
          </StatNumber>
          <StatHelpText fontSize="xs">Combined worth</StatHelpText>
        </Stat>

        <Stat bg={statBg} p={4} borderRadius="md" borderWidth="1px" borderColor={borderColor}>
          <StatLabel fontSize="xs">High-Value Items (A)</StatLabel>
          <StatNumber fontSize="2xl">{summary.countA}</StatNumber>
          <StatHelpText fontSize="xs">
            ${summary.valueA.toLocaleString()} ({pctText(share(summary.valueA))})
          </StatHelpText>
        </Stat>

        <Stat bg={statBg} p={4} borderRadius="md" borderWidth="1px" borderColor={borderColor}>
          <StatLabel fontSize="xs">Coverage Ratio</StatLabel>
          <StatNumber fontSize="2xl">
            {pctText(pctOf(summary.countA, totalItems), 0)}
          </StatNumber>
          <StatHelpText fontSize="xs">A-items coverage</StatHelpText>
        </Stat>
      </SimpleGrid>

      {/* --- UPDATE: FlowSense AI explainer */}
      <Box mb={6}>
        <FlowSenseAIExplainer
          module="inventory_abc"
          title="Explain this ABC split"
          subtitle="Where inventory value is concentrated"
          facts={abcExplainer.facts}
          findings={abcExplainer.findings}
          questions={['Which A-class items need tighter stock control?', 'Is our split healthy compared with the 80/15/5 rule?', 'Where can we free up cash in C-class stock?']}
        />
      </Box>
      {/* --- END UPDATE */}

      {/* Charts Section */}
      <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={6} mb={6}>
        <Box
          p={4}
          borderWidth="1px"
          borderColor={borderColor}
          borderRadius="md"
          bg={cardBg}
        >
          <Heading size="sm" mb={4}>
            Value Distribution
          </Heading>
          <ReactApexChart
            options={donutChartOptions}
            series={donutChartSeries}
            type="donut"
            height={320}
          />
        </Box>

        <Box
          p={4}
          borderWidth="1px"
          borderColor={borderColor}
          borderRadius="md"
          bg={cardBg}
        >
          <Heading size="sm" mb={4}>
            Item Count by Category
          </Heading>
          <ReactApexChart
            options={barChartOptions}
            series={barChartSeries}
            type="bar"
            height={320}
          />
        </Box>
      </SimpleGrid>

      {/* Category Details */}
      <Box
        p={4}
        borderWidth="1px"
        borderColor={borderColor}
        borderRadius="md"
        bg={cardBg}
      >
        <Heading size="sm" mb={4}>
          Category Breakdown
        </Heading>

        <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4} mb={6}>
          <Box p={4} bg={catBg.A} borderRadius="md" borderLeftWidth="4px" borderLeftColor="blue.500">
            <HStack justify="space-between" mb={2}>
              <Text fontWeight="bold" color="blue.700">
                A-Class Items
              </Text>
              <Badge colorScheme="blue">{summary.countA} items</Badge>
            </HStack>
            <Text fontSize="2xl" fontWeight="bold" color="blue.600">
              ${summary.valueA.toLocaleString()}
            </Text>
            <Progress
              value={share(summary.valueA) ?? 0}
              colorScheme="blue"
              size="sm"
              mt={2}
              borderRadius="full"
            />
            <Text fontSize="xs" color={catText} mt={1}>
              {pctText(share(summary.valueA))} of total value
            </Text>
          </Box>

          <Box p={4} bg={catBg.B} borderRadius="md" borderLeftWidth="4px" borderLeftColor="teal.500">
            <HStack justify="space-between" mb={2}>
              <Text fontWeight="bold" color="teal.700">
                B-Class Items
              </Text>
              <Badge colorScheme="teal">{summary.countB} items</Badge>
            </HStack>
            <Text fontSize="2xl" fontWeight="bold" color="teal.600">
              ${summary.valueB.toLocaleString()}
            </Text>
            <Progress
              value={share(summary.valueB) ?? 0}
              colorScheme="teal"
              size="sm"
              mt={2}
              borderRadius="full"
            />
            <Text fontSize="xs" color={catText} mt={1}>
              {pctText(share(summary.valueB))} of total value
            </Text>
          </Box>

          <Box p={4} bg={catBg.C} borderRadius="md" borderLeftWidth="4px" borderLeftColor="orange.500">
            <HStack justify="space-between" mb={2}>
              <Text fontWeight="bold" color="orange.700">
                C-Class Items
              </Text>
              <Badge colorScheme="orange">{summary.countC} items</Badge>
            </HStack>
            <Text fontSize="2xl" fontWeight="bold" color="orange.600">
              ${summary.valueC.toLocaleString()}
            </Text>
            <Progress
              value={share(summary.valueC) ?? 0}
              colorScheme="orange"
              size="sm"
              mt={2}
              borderRadius="full"
            />
            <Text fontSize="xs" color={catText} mt={1}>
              {pctText(share(summary.valueC))} of total value
            </Text>
          </Box>
        </SimpleGrid>

        {/* Item Tables */}
        <Tabs variant="enclosed" colorScheme="blue">
          <TabList>
            <Tab>
              <Icon as={FaDollarSign} mr={2} />
              A-Class Items
            </Tab>
            <Tab>
              <Icon as={FaWarehouse} mr={2} />
              B-Class Items
            </Tab>
            <Tab>
              <Icon as={FaBox} mr={2} />
              C-Class Items
            </Tab>
          </TabList>

          <TabPanels>
            <TabPanel>
              <ItemTable
                items={analysis.itemsA || []}
                category="A"
                onItemClick={(item) => handleItemClick(item, 'A')}
              />
            </TabPanel>
            <TabPanel>
              <ItemTable
                items={analysis.itemsB || []}
                category="B"
                onItemClick={(item) => handleItemClick(item, 'B')}
              />
            </TabPanel>
            <TabPanel>
              <ItemTable
                items={analysis.itemsC || []}
                category="C"
                onItemClick={(item) => handleItemClick(item, 'C')}
              />
            </TabPanel>
          </TabPanels>
        </Tabs>
      </Box>

      {/* Item Details Modal */}
      <ItemDetailsModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        item={selectedItem}
        category={selectedCategory}
      />
    </Card>
  );
};

export default ABCAnalysisWidget;